import { api, ConflictError } from "./api";

// CockpitStorage is declared globally in src/global.d.ts.
type CockpitStorage = Window["storage"];

// Legacy components use a `window.storage.get/set/delete` API that has no
// notion of versions. The server now requires `If-Match: <version>` on PUT
// for optimistic concurrency. We bridge this by caching the last-seen
// version per key client-side and echoing it back on the next set.
//
// On 412 (Conflict) we surface a callback so the app shell can show a
// toast: "Someone else just saved this, reload or overwrite?". By default
// the conflict callback is `console.warn`; main.tsx wires it up to a real
// toast UI.
const versions = new Map<string, number>();
let onConflict: (key: string, message: string) => Promise<"reload" | "overwrite"> = async (
  key,
  msg,
) => {
  console.warn("[storage] conflict on", key, msg);
  return "reload"; // safe default
};

export function setConflictHandler(
  handler: (key: string, message: string) => Promise<"reload" | "overwrite">,
): void {
  onConflict = handler;
}

// Writes per key are chained so a PUT only starts after the previous one
// returned its new version. Without this, two quick saves from the same tab
// both send the same If-Match and the second one 412s against ourselves.
const writeQueues = new Map<string, Promise<unknown>>();
function enqueueWrite<T>(key: string, fn: () => Promise<T>): Promise<T> {
  const prev = writeQueues.get(key) ?? Promise.resolve();
  const next = prev.catch(() => {}).then(fn);
  writeQueues.set(key, next);
  return next;
}

export function createStorage(): CockpitStorage {
  return {
    async get(key) {
      const result = await api.getState<unknown>(key);
      if (result === null) return null;
      versions.set(key, result.version);
      const v = result.value;
      // Legacy code expects `value` to be a JSON-encoded string that it can
      // `JSON.parse`. Backend already parses; re-stringify for the legacy
      // contract.
      return { value: typeof v === "string" ? v : JSON.stringify(v) };
    },

    set(key, value) {
      return enqueueWrite(key, () => putVersioned(key, value));
    },

    async delete(key) {
      await api.deleteState(key);
      versions.delete(key);
    },
  };

  async function putVersioned(key: string, value: string): Promise<void> {
    // The legacy contract passes an already-stringified JSON. Parse once
    // so the backend stores normalized JSON, not a quoted string.
    let parsed: unknown = value;
    try {
      parsed = JSON.parse(value);
    } catch {
      // Plain-string state (e.g. timestamps); store as-is.
    }

    const lastVersion = versions.get(key) ?? 0;
    try {
      const res = await api.putState(key, parsed, lastVersion);
      versions.set(key, res.version);
    } catch (err) {
      if (err instanceof ConflictError) {
        const choice = await onConflict(key, err.message);
        if (choice === "overwrite") {
          // Refetch to learn current version, then retry with that.
          const current = await api.getState<unknown>(key);
          const v = current?.version ?? 0;
          const res = await api.putState(key, parsed, v);
          versions.set(key, res.version);
          return;
        }
        // "reload": drop our cached version + rethrow so the caller can
        // re-read state (the legacy code typically swallows this in its
        // `try { } catch (e) {}` blocks, which is fine — the user sees
        // the toast and reloads manually).
        versions.delete(key);
        throw err;
      }
      throw err;
    }
  }
}
